/**
 * BottleScene
 * ------------
 * R3F (react-three-fiber) does not tolerate arbitrary string props leaking
 * onto Three.js host primitives — and the visual-edits Babel plugin injects
 * such props (`x-line-number`, `x-component`, `x-source-file`, …) into every
 * uppercase JSX element it sees. To get a readable JSX scene **and** keep the
 * plugin happy, we wrap each R3F host primitive (and each drei component) in
 * a tiny Pascal-case `Safe(...)` adapter that:
 *
 *   1. Receives any plugin-injected metadata as plain React props.
 *   2. Strips every `x-*` and `data-ve*` key.
 *   3. Forwards only the cleaned props (and refs) to the underlying R3F
 *      primitive via `React.createElement`, which the plugin never visits.
 *
 * The result: the scene below is plain JSX, refs work natively, and Three.js
 * never receives metadata it cannot interpret.
 */
import React, { Suspense, forwardRef, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { ContactShadows, Environment, OrbitControls } from '@react-three/drei';

// ---------- Visual-edits prop sanitiser -----------------------------------
const isVEProp = (key) => key.startsWith('x-') || key.startsWith('data-ve');
const stripVE = (props) => {
  const out = {};
  for (const k in props) if (!isVEProp(k)) out[k] = props[k];
  return out;
};

/**
 * `safe(target)` returns a Pascal-case wrapper component. It strips visual-
 * edits metadata and forwards everything else (children + ref) to `target`,
 * which can be either an R3F host string ('mesh', 'group', …) or a real
 * component (Canvas, OrbitControls, …).
 */
const safe = (target) => {
  const Wrapped = forwardRef(function SafeWrapper(
    { children, ...rest },
    ref,
  ) {
    return React.createElement(
      target,
      ref ? { ...stripVE(rest), ref } : stripVE(rest),
      children,
    );
  });
  Wrapped.displayName = `Safe(${
    typeof target === 'string'
      ? target
      : target.displayName || target.name || 'Component'
  })`;
  return Wrapped;
};

// R3F host primitives used in this scene
const Group = safe('group');
const Mesh = safe('mesh');
const MeshPhysicalMaterial = safe('meshPhysicalMaterial');
const AmbientLight = safe('ambientLight');
const DirectionalLight = safe('directionalLight');
const PointLight = safe('pointLight');

// drei / R3F components — wrapped so plugin metadata is also stripped before
// it reaches their internal Three.js objects.
const SafeCanvas = safe(Canvas);
const SafeOrbitControls = safe(OrbitControls);
const SafeEnvironment = safe(Environment);
const SafeContactShadows = safe(ContactShadows);

// ---------- Bottle profile (rotational, y from base 0 to cap top ~3.0) ----
const OUTER_PROFILE = [
  [0.0, 0.05],
  [0.02, 0.62],
  [0.06, 0.86],
  [0.12, 0.96],
  [0.3, 1.0],
  [1.95, 1.0],
  [2.1, 0.97],
  [2.3, 0.86],
  [2.55, 0.55],
  [2.85, 0.5],
  [2.92, 0.55],
];

const INNER_PROFILE = OUTER_PROFILE
  .filter(([y]) => y < 2.6)
  .map(([y, r]) => [Math.max(0.02, y), Math.max(0, r - 0.07)]);

const LIQUID_Y_MIN = 0.06;
const LIQUID_Y_MAX = 1.95;

const buildLathe = (profile, segments = 96) => {
  const pts = profile.map(([y, r]) => new THREE.Vector2(r, y));
  return new THREE.LatheGeometry(pts, segments);
};

const innerRadiusAt = (y) => {
  for (let i = 0; i < INNER_PROFILE.length - 1; i++) {
    const [y1, r1] = INNER_PROFILE[i];
    const [y2, r2] = INNER_PROFILE[i + 1];
    if (y >= y1 && y <= y2) {
      const t = (y - y1) / Math.max(1e-6, y2 - y1);
      return r1 + (r2 - r1) * t;
    }
  }
  return 0.9;
};

// ---------- Bottle ---------------------------------------------------------
function Bottle({ targetLevel = 0.36 }) {
  const meniscusRef = useRef();
  const currentLevel = useRef(targetLevel);

  const clipPlane = useMemo(
    () => new THREE.Plane(new THREE.Vector3(0, -1, 0), LIQUID_Y_MIN),
    [],
  );
  const outerGeo = useMemo(() => buildLathe(OUTER_PROFILE), []);
  const liquidGeo = useMemo(() => buildLathe(INNER_PROFILE), []);

  // Pump head geometry — replaces the previous screw cap.
  // Layers (from bottom up):
  //   • crimp collar  — a stout cylinder hugging the neck
  //   • brass ferrule — a slim gold (Surya) ring atop the collar
  //   • lock ring     — a silver (Chandra) ring above the ferrule
  //   • neck riser    — the thin vertical column the actuator sits on
  //   • actuator      — the broader pressable head
  //   • nozzle tip    — the small frusto-conic spout
  const pumpCollarGeo = useMemo(
    () => new THREE.CylinderGeometry(0.58, 0.58, 0.18, 64),
    [],
  );
  const pumpBrassRingGeo = useMemo(           // Surya · gold
    () => new THREE.CylinderGeometry(0.55, 0.55, 0.045, 64),
    [],
  );
  const pumpSilverRingGeo = useMemo(          // Chandra · silver
    () => new THREE.CylinderGeometry(0.52, 0.52, 0.035, 64),
    [],
  );
  const pumpRiserGeo = useMemo(
    () => new THREE.CylinderGeometry(0.12, 0.12, 0.20, 48),
    [],
  );
  const pumpActuatorGeo = useMemo(
    () => new THREE.CylinderGeometry(0.42, 0.42, 0.22, 64),
    [],
  );
  const pumpActuatorTopGeo = useMemo(
    () => new THREE.CylinderGeometry(0.40, 0.42, 0.04, 64),
    [],
  );
  const pumpNozzleGeo = useMemo(
    () => new THREE.CylinderGeometry(0.06, 0.11, 0.22, 32),
    [],
  );

  const labelGeo = useMemo(
    () => new THREE.CylinderGeometry(1.005, 1.005, 0.55, 96, 1, true),
    [],
  );
  const meniscusGeo = useMemo(() => new THREE.CircleGeometry(1, 96), []);

  useFrame((state, dt) => {
    const t = Math.min(1, dt * 2.6);
    currentLevel.current += (targetLevel - currentLevel.current) * t;
    const y =
      LIQUID_Y_MIN + currentLevel.current * (LIQUID_Y_MAX - LIQUID_Y_MIN);
    clipPlane.constant = y;

    if (meniscusRef.current) {
      const time = state.clock.elapsedTime;
      const r = innerRadiusAt(y) * 0.985;
      meniscusRef.current.position.y = y + Math.sin(time * 1.4) * 0.004;
      meniscusRef.current.scale.set(
        r + Math.sin(time * 2.1) * 0.004,
        1,
        r + Math.cos(time * 1.7) * 0.004,
      );
      meniscusRef.current.visible = currentLevel.current > 0.01;
    }
  });

  return (
    <Group>
      {/* Outer glass */}
      <Mesh geometry={outerGeo} castShadow>
        <MeshPhysicalMaterial
          color="#F4F8F9"
          transmission={0.96}
          transparent
          opacity={1}
          roughness={0.04}
          metalness={0}
          ior={1.46}
          thickness={0.55}
          clearcoat={1}
          clearcoatRoughness={0.04}
          attenuationColor={new THREE.Color('#90B0BF')}
          attenuationDistance={1.6}
          envMapIntensity={1.1}
          side={THREE.DoubleSide}
        />
      </Mesh>

      {/* Liquid */}
      <Mesh geometry={liquidGeo}>
        <MeshPhysicalMaterial
          color="#AAC6D4"
          transmission={0.45}
          transparent
          opacity={1}
          roughness={0.18}
          metalness={0}
          ior={1.34}
          thickness={1.1}
          clearcoat={0.7}
          clearcoatRoughness={0.15}
          attenuationColor={new THREE.Color('#7C9CB1')}
          attenuationDistance={2.0}
          envMapIntensity={0.8}
          clippingPlanes={[clipPlane]}
          clipShadows
          side={THREE.DoubleSide}
        />
      </Mesh>

      {/* Meniscus surface */}
      <Mesh
        ref={meniscusRef}
        geometry={meniscusGeo}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, LIQUID_Y_MIN, 0]}
      >
        <MeshPhysicalMaterial
          color="#C1D7E2"
          transmission={0.25}
          roughness={0.06}
          metalness={0}
          clearcoat={1}
          clearcoatRoughness={0.04}
          ior={1.34}
          envMapIntensity={1}
          transparent
          opacity={0.85}
        />
      </Mesh>

      {/* Pump head — replaces the screw cap. Surya (gold) ferrule +
          Chandra (silver) lock ring sit atop a Miron-blue crimp collar.
          The slim riser + actuator + nozzle frame an actuator the user
          can read as "pressable". */}
      <Group position={[0, 2.95, 0]}>
        {/* Crimp collar */}
        <Mesh geometry={pumpCollarGeo}>
          <MeshPhysicalMaterial
            color="#3C5260"
            metalness={0.5}
            roughness={0.42}
            clearcoat={1}
            clearcoatRoughness={0.18}
            envMapIntensity={1.1}
          />
        </Mesh>
        {/* Surya · gold brass ferrule */}
        <Mesh geometry={pumpBrassRingGeo} position={[0, 0.115, 0]}>
          <MeshPhysicalMaterial
            color="#D4AF5A"
            metalness={0.85}
            roughness={0.22}
            clearcoat={1}
            clearcoatRoughness={0.1}
            envMapIntensity={1.4}
          />
        </Mesh>
        {/* Chandra · silver lock ring */}
        <Mesh geometry={pumpSilverRingGeo} position={[0, 0.165, 0]}>
          <MeshPhysicalMaterial
            color="#D7DDE0"
            metalness={0.9}
            roughness={0.18}
            clearcoat={1}
            clearcoatRoughness={0.08}
            envMapIntensity={1.3}
          />
        </Mesh>
        {/* Riser stem */}
        <Mesh geometry={pumpRiserGeo} position={[0, 0.30, 0]}>
          <MeshPhysicalMaterial
            color="#90B0BF"
            metalness={0.7}
            roughness={0.22}
            envMapIntensity={1.2}
          />
        </Mesh>
        {/* Actuator body */}
        <Mesh geometry={pumpActuatorGeo} position={[0, 0.51, 0]}>
          <MeshPhysicalMaterial
            color="#7C9CB1"
            metalness={0.55}
            roughness={0.3}
            clearcoat={1}
            clearcoatRoughness={0.15}
            envMapIntensity={1.2}
          />
        </Mesh>
        {/* Actuator top cap (slightly tapered) */}
        <Mesh geometry={pumpActuatorTopGeo} position={[0, 0.64, 0]}>
          <MeshPhysicalMaterial
            color="#90B0BF"
            metalness={0.6}
            roughness={0.22}
            envMapIntensity={1.2}
          />
        </Mesh>
        {/* Nozzle — angled spout */}
        <Mesh
          geometry={pumpNozzleGeo}
          position={[0.40, 0.51, 0]}
          rotation={[0, 0, Math.PI / 2.05]}
        >
          <MeshPhysicalMaterial
            color="#3C5260"
            metalness={0.7}
            roughness={0.22}
            clearcoat={1}
            clearcoatRoughness={0.12}
            envMapIntensity={1.2}
          />
        </Mesh>
      </Group>

      {/* Subtle label band on bottle */}
      <Mesh geometry={labelGeo} position={[0, 1.05, 0]}>
        <MeshPhysicalMaterial
          color="#F4F8F9"
          metalness={0}
          roughness={0.45}
          transmission={0.05}
          clearcoat={0.4}
          side={THREE.DoubleSide}
          opacity={0.92}
          transparent
        />
      </Mesh>
    </Group>
  );
}

// ---------- Stage ----------------------------------------------------------
function Stage({ targetLevel }) {
  return (
    <>
      <AmbientLight intensity={0.35} />
      <DirectionalLight
        position={[4, 6, 5]}
        intensity={1.4}
        castShadow
        shadow-mapSize={[1024, 1024]}
      />
      <PointLight position={[-3, 3, -4]} intensity={0.9} color="#C1D7E2" />
      <PointLight position={[3, 1, -2]} intensity={0.4} color="#AAC6D4" />
      <Bottle targetLevel={targetLevel} />
      <SafeContactShadows
        position={[0, 0, 0]}
        opacity={0.45}
        scale={6}
        blur={2.6}
        far={4}
        color="#7C9CB1"
      />
      <SafeEnvironment preset="studio" />
    </>
  );
}

// ---------- Scene ----------------------------------------------------------
export default function BottleScene({ targetLevel = 0.36 }) {
  return (
    <SafeCanvas
      data-testid="twin-canvas"
      shadows
      dpr={[1, 1.6]}
      gl={{ antialias: true, alpha: true }}
      camera={{ position: [3.6, 2.8, 5.4], fov: 32 }}
      onCreated={({ gl }) => {
        gl.localClippingEnabled = true;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.05;
      }}
      style={{ width: '100%', height: '100%', background: 'transparent' }}
    >
      <Suspense fallback={null}>
        <Stage targetLevel={targetLevel} />
      </Suspense>
      <SafeOrbitControls
        target={[0, 1.75, 0]}
        enableZoom={false}
        enablePan={false}
        autoRotate
        autoRotateSpeed={0.6}
        minPolarAngle={Math.PI / 3}
        maxPolarAngle={Math.PI / 2.05}
      />
    </SafeCanvas>
  );
}
